import { GetStaticPropsContext } from 'next';
import { Container, Grid } from '@mui/material';
import fetcher from 'lib/fetcher';
import { Header } from 'components/Header/Header';
import { Footer } from 'components/Footer/Footer';
import { MetaHead } from 'components/MetaHead/MetaHead';
import { LegalDocsMenu } from 'components/LegalDocs/LegalDocsMenu';
import { legalMenuItems } from 'components/LegalDocs/legalMenuItems';
import { slugToUrl } from 'lib/contentful/slugHelper';
import { LegalDoc } from 'components/LegalDocs/LegalDoc';
import { FIFTEEN_MINUTES } from 'lib/constants/revalidation';

interface LegalPageProps {
  url: string;
  legalDocument: any;
  pdfUrl: string | null;
  title: string;
}

export default function LegalPage(props: LegalPageProps) {
  const { url, legalDocument, pdfUrl, title } = props;
  const germanDocument = legalDocument?.document['de-DE'];

  return (
    <>
      <MetaHead
        url={url}
        title={title}
        index={false}
        follow={false}
        datePublished={legalDocument.createdAt}
        dateModified={legalDocument.updatedAt}
      />
      <Header headerStyle="full" />
      <Container maxWidth="lg">
        <Grid container>
          <Grid item xs={12} md={3}>
            <LegalDocsMenu menuItems={legalMenuItems} />
          </Grid>

          <Grid item xs={12} md={9}>
            <LegalDoc document={germanDocument} pdfLink={pdfUrl} />
          </Grid>
        </Grid>
      </Container>

      <Footer footerStyle="full" />
    </>
  );
}

export const getStaticProps = async ({ params }: GetStaticPropsContext) => {
  const url = slugToUrl('/uber-uns/', params!.slug!); // Router guarantees there is a slug
  const legalDocInfo = legalMenuItems.find((item) => item.url === url);

  if (!legalDocInfo?.legalDocName) {
    return { notFound: true, revalidate: FIFTEEN_MINUTES };
  }

  const legalHost = 'https://legal-docs.finanzcheck.de';
  const legalDocument = await fetcher(
    `${legalHost}/${legalDocInfo.legalDocName}.json`,
  );
  const pdfUrl = legalDocInfo.showPdf
    ? `${legalHost}/${legalDocInfo.legalDocName}.pdf`
    : null;

  return {
    props: {
      url,
      legalDocument,
      pdfUrl,
      title: legalDocInfo.title,
    },
    revalidate: FIFTEEN_MINUTES,
  };
};

// Get all valid slugs for THIS LEVEL ONLY!
// Fallback false says display 404 if your slug is not found on build time
// Fallback true says generate the page on demand if your slug is not found on build time
export const getStaticPaths = () => {
  return {
    paths: [
      {
        params: { slug: ['agb'] },
      },
      {
        params: { slug: ['datenschutz'] },
      },
      {
        params: { slug: ['impressum'] },
      },
      {
        params: { slug: ['einwilligungserklarung'] },
      },
      {
        params: { slug: ['unsere-partner', 'liste'] },
      },
      {
        params: {
          slug: ['pflichtinformation', 'vorvertragliche-informationen'],
        },
      },
    ],
    fallback: 'blocking',
  };
};
